import {
  BANNED_CUSTOMER_SORTS,
  BANNED_REMINDER_FILTERS,
  DEBT_AGE_BUCKETS,
  SORT_DIRS,
  type BannedCustomerSort,
  type BannedReminderFilter,
  type DebtAgeBucket,
  type SortDir,
} from '@gozoki/api-types'
import { centRound } from '@gozoki/tools'
import { z } from 'zod'
import { DEFAULT_PAGE_SIZE, optionalCaught, pageSchema, pageSizeSchema } from './customers-search'

export { selectionSummary } from './customers-search'

/**
 * Filtres de l'écran Clients bannis (`/users/payment-defaults`), portés par le `search` de la
 * route. Pur et testé (`banned-customers-search.test.mjs`). Tranches d'ancienneté, tris et filtre
 * de relance sont ceux du contrat `GET /customers/banned`, partagés avec le backend par
 * `@gozoki/api-types`.
 */

export const DEBT_AGE_LABELS: Record<DebtAgeBucket, string> = {
  lt7: '< 7 j',
  '7to30': '7–30 j',
  '30to90': '30–90 j',
  gt90: '> 90 j',
}

export const bannedCustomersSearchSchema = z.object({
  q: optionalCaught(z.string()),
  storeId: optionalCaught(z.number().int().positive()),
  minAmount: optionalCaught(z.number().min(0)),
  debtAge: optionalCaught(z.enum(DEBT_AGE_BUCKETS)),
  reminded: optionalCaught(z.enum(BANNED_REMINDER_FILTERS)),
  sortBy: optionalCaught(z.enum(BANNED_CUSTOMER_SORTS)),
  sortDir: optionalCaught(z.enum(SORT_DIRS)),
  page: pageSchema,
  pageSize: pageSizeSchema,
})

export type BannedCustomersSearch = z.infer<typeof bannedCustomersSearchSchema>

export interface BannedCustomersListQuery {
  page: number
  pageSize: number
  search?: string
  storeId?: number
  minAmount?: number
  debtAge?: DebtAgeBucket
  reminded?: BannedReminderFilter
  sortBy?: BannedCustomerSort
  sortDir?: SortDir
}

/** Tri par défaut : montant dû décroissant avec la solvabilité, ancienneté croissante sinon. */
export const defaultBannedSort = (
  canReadSolvency: boolean,
): { sortBy: BannedCustomerSort; sortDir: SortDir } =>
  canReadSolvency
    ? { sortBy: 'amount', sortDir: 'desc' }
    : { sortBy: 'bannedSince', sortDir: 'asc' }

/**
 * Paramètres de `GET /customers/banned`. Le montant dû (filtre, tri) relève de la solvabilité :
 * sans le droit, il n'est pas envoyé (le serveur le refuserait en 403).
 */
export const searchToBannedQuery = (
  search: BannedCustomersSearch,
  canReadSolvency: boolean,
): BannedCustomersListQuery => {
  const q = search.q?.trim()
  const fallback = defaultBannedSort(canReadSolvency)
  const sortBy =
    search.sortBy === undefined || (search.sortBy === 'amount' && !canReadSolvency)
      ? fallback.sortBy
      : search.sortBy
  const sortDir = search.sortDir ?? (sortBy === fallback.sortBy ? fallback.sortDir : 'asc')
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE,
    ...(q ? { search: q } : {}),
    ...(search.storeId === undefined ? {} : { storeId: search.storeId }),
    ...(search.minAmount === undefined || !canReadSolvency ? {} : { minAmount: search.minAmount }),
    ...(search.debtAge === undefined ? {} : { debtAge: search.debtAge }),
    ...(search.reminded === undefined ? {} : { reminded: search.reminded }),
    sortBy,
    sortDir,
  }
}

export const applyBannedFilter = (
  search: BannedCustomersSearch,
  change: Partial<BannedCustomersSearch>,
): BannedCustomersSearch => ({ ...search, ...change, page: undefined })

export const hasActiveBannedFilters = (search: BannedCustomersSearch): boolean =>
  search.storeId !== undefined ||
  search.minAmount !== undefined ||
  search.debtAge !== undefined ||
  search.reminded !== undefined ||
  (search.q?.trim().length ?? 0) > 0

export const clearBannedFilters = (search: BannedCustomersSearch): BannedCustomersSearch => ({
  pageSize: search.pageSize,
  sortBy: search.sortBy,
  sortDir: search.sortDir,
})

const BANNED_SORT_LABELS: Record<BannedCustomerSort, string> = {
  amount: 'montant dû',
  bannedSince: 'ancienneté du défaut',
  unpaidCarts: 'paniers impayés',
  lastReminder: 'dernière relance',
  name: 'nom',
}

/** « tri : montant dû décroissant » */
export const bannedSortLabel = (sortBy: BannedCustomerSort, sortDir: SortDir): string =>
  `tri : ${BANNED_SORT_LABELS[sortBy]} ${sortDir === 'desc' ? 'décroissant' : 'croissant'}`

/** Somme des dettes des lignes sélectionnées ; `null` dès qu'une dette est masquée. */
export const selectedAmountDue = (
  items: readonly { id: number; unBanAmount?: number | null }[],
  selectedIds: readonly number[],
): number | null => {
  const selected = items.filter((item) => selectedIds.includes(item.id))
  if (selected.some((item) => item.unBanAmount === undefined)) return null
  return centRound(selected.reduce((sum, item) => sum + (item.unBanAmount ?? 0), 0))
}
